import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Box, Button, Paper, TextField, Typography, Alert, Chip } from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import api from "../Api";
import { parseSeals } from "../utils/seals";

// Add seals to the persistent pool (mirrors Myntra HMB "Add Seals").
export default function SealAddCard() {
  const [raw, setRaw] = useState("");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [pool, setPool] = useState({ unused: null, total: null });

  const count = useMemo(() => parseSeals(raw).length, [raw]);

  const refreshPool = useCallback(async () => {
    try {
      const { data } = await api.get("/api/seals/count");
      setPool({ unused: data.unused ?? 0, total: data.total ?? 0 });
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => { refreshPool(); }, [refreshPool]);

  const addToPool = async () => {
    if (!parseSeals(raw).length) { setError("Enter at least one seal number."); return; }
    setError(""); setMsg(""); setAdding(true);
    try {
      const { data } = await api.post("/api/seals/add", { seals: raw });
      setMsg(data.message || `Added ${data.added} seal(s).`);
      setPool(p => ({ ...p, unused: data.unused ?? p.unused }));
      refreshPool();
      setRaw("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to add seals.");
    } finally {
      setAdding(false);
    }
  };

  const poolColor = pool.unused == null ? "default"
    : pool.unused >= 50 ? "success" : pool.unused >= 10 ? "warning" : "error";

  return (
    <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", boxShadow: "none", height: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>🏷 Add Seals to Pool</Typography>
        <Chip size="small" color={poolColor}
          label={pool.unused == null ? "Pool: …" : `Unused: ${pool.unused}`} sx={{ fontWeight: 700 }} />
      </Box>
      <TextField
        fullWidth multiline minRows={2} size="small"
        label="Seal numbers — range or list"
        placeholder="MP6001-MP6100  |  MP6001,MP6003"
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) addToPool(); }}
      />
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      {msg && <Alert severity="success" sx={{ mt: 1 }} onClose={() => setMsg("")}>{msg}</Alert>}
      <Box sx={{ mt: 1.5 }}>
        <Button variant="contained" startIcon={<AddCircleIcon />} disabled={adding || count === 0} onClick={addToPool}>
          {adding ? "Adding…" : `Add to Pool${count ? ` (${count})` : ""}`}
        </Button>
      </Box>
    </Paper>
  );
}
